"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import { Heart, ShoppingBag, Star } from "lucide-react";

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  originalPrice?: number;
  image: string;
  category: string;
  rating: number;
  reviews: number;
  isNew?: boolean;
  aspectRatio: "tall" | "square" | "wide";
}

interface ProductCardProps {
  product: Product;
  index: number;
}

export default function ProductCard({ product, index }: ProductCardProps) {
  const [isLiked, setIsLiked] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const aspectRatioClasses = {
    tall: "aspect-[3/4]",
    square: "aspect-square",
    wide: "aspect-[4/3]",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.6,
        delay: index * 0.1,
        ease: [0.25, 0.1, 0.25, 1],
      }}
      className="group relative break-inside-avoid"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* 卡片容器 */}
      <div className="relative overflow-hidden rounded-xl bg-white">
        {/* 图片容器 */}
        <div className={`relative ${aspectRatioClasses[product.aspectRatio]} overflow-hidden`}>
          <Image
            src={product.image}
            alt={product.name}
            fill
            className="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.02]"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />

          {/* 新品标签 */}
          {product.isNew && (
            <div className="absolute left-3 top-3 rounded-full bg-[#3B82F6] px-3 py-1 text-xs font-medium text-white">
              新品
            </div>
          )}

          {/* 收藏按钮 */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsLiked(!isLiked);
            }}
            className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 backdrop-blur-sm transition-all duration-300 hover:scale-110 hover:bg-white"
          >
            <motion.div
              animate={isLiked ? { scale: [1, 1.2, 1] } : { scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Heart
                className={`h-4 w-4 transition-colors duration-300 ${
                  isLiked ? "fill-[#3B82F6] stroke-[#3B82F6]" : "stroke-[#1A1A1A]"
                }`}
              />
            </motion.div>
          </button>

          {/* 悬停遮罩和快速操作 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isHovered ? 1 : 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 flex items-end justify-center bg-gradient-to-t from-black/40 via-transparent to-transparent p-4"
          >
            <motion.button
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: isHovered ? 0 : 20, opacity: isHovered ? 1 : 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-white py-3 text-sm font-medium text-[#1A1A1A] transition-colors hover:bg-[#F1F5F9]"
            >
              <ShoppingBag className="h-4 w-4" />
              加入购物车
            </motion.button>
          </motion.div>
        </div>

        {/* 产品信息 */}
        <div className="space-y-2 p-4">
          {/* 分类标签 */}
          <div className="text-xs font-medium tracking-wide text-[#94A3B8] uppercase">
            {product.category}
          </div>

          {/* 产品名称 */}
          <h3 className="text-base font-medium text-[#1A1A1A] transition-colors group-hover:text-[#3B82F6]">
            {product.name}
          </h3>

          {/* 产品描述 */}
          <p className="line-clamp-2 text-sm leading-relaxed text-[#64748B]">
            {product.description}
          </p>

          {/* 评分 */}
          <div className="flex items-center gap-1">
            <Star className="h-3.5 w-3.5 fill-[#3B82F6] stroke-[#3B82F6]" />
            <span className="text-sm font-medium text-[#1A1A1A]">{product.rating}</span>
            <span className="text-xs text-[#94A3B8]">({product.reviews})</span>
          </div>

          {/* 价格 */}
          <div className="flex items-center gap-2 pt-1">
            <span
              className="text-lg font-semibold text-[#3B82F6]"
              style={{ fontFamily: "'Space Grotesk', monospace" }}
            >
              ¥{product.price.toLocaleString()}
            </span>
            {product.originalPrice && (
              <span
                className="text-sm text-[#94A3B8] line-through"
                style={{ fontFamily: "'Space Grotesk', monospace" }}
              >
                ¥{product.originalPrice.toLocaleString()}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
